import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import SEO from '../components/SEO';
import StaticMap from '../components/StaticMap';
import { openPackingSlip } from '../components/PackingSlip';
import { useSiteSettings } from '../context/SiteSettingsContext';
import { useCart } from '../context/CartContext';
import { formatPrice } from '../utils/format';
import { Button, Skeleton } from '../components/primitives';
import './OrderTracker.css';

const OrderTracker = () => {
  const { t, i18n } = useTranslation();
  const siteSettings = useSiteSettings();
  const { token } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchOrder = async () => {
      try {
        const response = await api.get(`/api/orders/track/${token}`);
        setOrder(response.data);
      } catch {
        setError(t('tracking.notFound', 'Order not found or invalid tracking link.'));
      } finally {
        setLoading(false);
      }
    };

    if (token) fetchOrder();
  }, [token, t]);

  const fmtPrice = (v) => formatPrice(v, { lang: i18n.language });

  const statusSteps = ['pending', 'processing', 'shipped', 'delivered'];

  const statusBadgeClass = (status) => {
    const map = {
      pending: 'status-pending',
      processing: 'status-processing',
      shipped: 'status-shipped',
      delivered: 'status-delivered',
      cancelled: 'status-cancelled',
    };
    return map[status] || 'status-pending';
  };

  const getCurrentStep = (status) => {
    const idx = statusSteps.indexOf(status);
    return idx >= 0 ? idx : 0;
  };

  if (loading) {
    return (
      <div className="tracker-page" aria-busy="true">
        <div className="container">
          <Skeleton.Block height="1.25rem" width="220px" rounded="pill" />
          <Skeleton.Block height="2rem" width="60%" rounded="md" style={{ marginTop: 'var(--space-3)' }} />
          <Skeleton.Block height="120px" rounded="md" style={{ marginTop: 'var(--space-4)' }} />
          <Skeleton.Block height="240px" rounded="md" style={{ marginTop: 'var(--space-4)' }} />
        </div>
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="tracker-page">
        <SEO title={t('tracking.title', 'Track Order')} />
        <div className="tracker-container">
          <div className="tracker-error">
            <h1>{t('tracking.title', 'Track Order')}</h1>
            <p>{error}</p>
            <Link to="/" className="tracker-home-link">{t('nav.home', 'Home')}</Link>
          </div>
        </div>
      </div>
    );
  }

  const currentStep = getCurrentStep(order.status);

  return (
    <div className="tracker-page">
      <SEO title={`${t('tracking.title', 'Track Order')} #${order.orderNumber}`} />

      <div className="tracker-container">
        <div className="tracker-header">
          <h1>{t('tracking.title', 'Track Order')}</h1>
          <div className="tracker-order-info">
            <span className="tracker-order-number">#{order.orderNumber}</span>
            <span className={`order-status-badge ${statusBadgeClass(order.status)}`}>
              {t(`order.status.${order.status}`, order.status)}
            </span>
          </div>
          <p className="tracker-date">
            {t('account.placed', 'Placed')} {new Date(order.createdAt).toLocaleDateString('en-US', {
              year: 'numeric', month: 'long', day: 'numeric'
            })}
          </p>
          <div className="tracker-actions">
            <Button
              variant="brand"
              size="md"
              onClick={() => {
                (order.items || []).forEach(item => {
                  addToCart({ id: item.productId || item.id, name: item.productName, slug: '', price: item.price, sku: item.productSku, images: [] }, item.quantity);
                });
                navigate('/checkout');
              }}
            >
              {t('account.reorder', 'Reorder')}
            </Button>
            <Button
              variant="secondary"
              size="md"
              onClick={() => openPackingSlip(order, siteSettings)}
            >
              {t('orderConfirmation.printOrder', 'Print Order')}
            </Button>
          </div>
        </div>

        {order.status !== 'cancelled' && (
          <div className="tracker-timeline">
            {statusSteps.map((step, i) => (
              <div key={step} className={`timeline-step ${i <= currentStep ? 'completed' : ''} ${i === currentStep ? 'current' : ''}`}>
                <div className="timeline-dot" />
                {i < statusSteps.length - 1 && <div className="timeline-line" />}
                <span className="timeline-label">{t(`order.status.${step}`, step)}</span>
                {i <= currentStep && (
                  <span className="timeline-date">
                    {step === 'pending' && order.createdAt ? new Date(order.createdAt).toLocaleDateString(i18n.language === 'sq' ? 'sq-AL' : 'en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''}
                    {step === 'processing' && order.updatedAt && currentStep >= 1 ? new Date(order.updatedAt).toLocaleDateString(i18n.language === 'sq' ? 'sq-AL' : 'en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''}
                    {step === 'shipped' && order.shippedAt ? new Date(order.shippedAt).toLocaleDateString(i18n.language === 'sq' ? 'sq-AL' : 'en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''}
                    {step === 'delivered' && order.deliveredAt ? new Date(order.deliveredAt).toLocaleDateString(i18n.language === 'sq' ? 'sq-AL' : 'en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''}
                  </span>
                )}
              </div>
            ))}
          </div>
        )}

        {order.status === 'cancelled' && order.cancellationReason && (
          <div className="tracker-banner tracker-banner--error">
            <strong>{t('tracking.cancellationReason', 'Cancellation Reason')}:</strong>{' '}
            <span>{order.cancellationReason}</span>
          </div>
        )}

        {(order.trackingNumber || order.shippingCarrier) && (
          <div className="tracker-banner">
            {order.shippingCarrier && (
              <div>
                <span className="tracker-banner__label">{t('tracking.carrier', 'Carrier')}</span>
                <span className="tracker-banner__value">{order.shippingCarrier}</span>
              </div>
            )}
            {order.trackingNumber && (
              <div>
                <span className="tracker-banner__label">{t('tracking.trackingNumber', 'Tracking Number')}</span>
                <span className="tracker-banner__value tracker-banner__value--mono">{order.trackingNumber}</span>
              </div>
            )}
          </div>
        )}

        <div className="tracker-grid">
          <div className="tracker-section">
            <h3>{t('orderConfirmation.items', 'Items')}</h3>
            {order.items?.map(item => (
              <div key={item.id} className="tracker-item">
                <div className="tracker-item-info">
                  <span className="tracker-item-name">{item.productName}</span>
                  <span className="tracker-item-sku">{item.productSku}</span>
                </div>
                <span className="tracker-item-qty">x{item.quantity}</span>
                <span className="tracker-item-price">{fmtPrice(item.subtotal)} {siteSettings.currencyLabel}</span>
              </div>
            ))}
          </div>

          <div className="tracker-sidebar">
            <div className="tracker-section">
              <h3>{t('common.total', 'Total')}</h3>
              <div className="tracker-totals">
                <div className="tracker-total-row">
                  <span>{t('common.subtotal', 'Subtotal')}</span>
                  <span>{fmtPrice(order.subtotal)} {siteSettings.currencyLabel}</span>
                </div>
                <div className="tracker-total-row">
                  <span>{t('common.shipping', 'Shipping')}</span>
                  <span>{parseFloat(order.shippingCost) === 0 ? t('common.freeShipping', 'Free') : `${fmtPrice(order.shippingCost)} ${siteSettings.currencyLabel}`}</span>
                </div>
                <div className="tracker-total-row" style={siteSettings.taxIncludedInPrice ? { color: '#64748b', fontSize: '0.875rem' } : undefined}>
                  <span>
                    {siteSettings.taxIncludedInPrice
                      ? t('common.taxIncluded', 'Tax (included)')
                      : t('common.tax', 'Tax')}
                  </span>
                  <span>{fmtPrice(order.tax)} {siteSettings.currencyLabel}</span>
                </div>
                <div className="tracker-total-row total-final">
                  <span>{t('common.total', 'Total')}</span>
                  <span>{fmtPrice(order.total)} {siteSettings.currencyLabel}</span>
                </div>
              </div>
            </div>

            <div className="tracker-section">
              <h3>{t('orderConfirmation.deliveryAddress', 'Delivery Address')}</h3>
              <p className="tracker-address">{order.deliveryAddress?.fullAddress}</p>
              {order.deliveryAddress?.latitude && order.deliveryAddress?.longitude && (
                <div className="tracker-map">
                  <StaticMap
                    latitude={order.deliveryAddress.latitude}
                    longitude={order.deliveryAddress.longitude}
                    width="100%"
                    height={150}
                  />
                </div>
              )}
            </div>

            {order.paymentMethod && (
              <div className="tracker-section">
                <h3>{t('payment.title', 'Payment')}</h3>
                <p className="tracker-payment">
                  {order.paymentMethod === 'cod' ? t('payment.cod', 'Cash on Delivery') : order.paymentMethod}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderTracker;
